import { sameServiceId } from 'src/entities/file/lib/service-table.utils';
import { OrderPdfStatus } from 'src/entities/order/model/order-status.model';
import {
  CreatePurchaseNoteInput,
  CreatedPurchaseNote
} from '../model/create-purchase-note.model';
import { ServicePurchaseNote } from '../model/purchase-note.model';

export type ApplyCreatedPurchaseNotesDefaults = Pick<
  CreatePurchaseNoteInput,
  'reviewDate' | 'purchaseNoteObservation' | 'reservationNumber'
>;

function buildPurchaseNoteSnapshot(
  created: CreatedPurchaseNote,
  defaults?: ApplyCreatedPurchaseNotesDefaults | null
): ServicePurchaseNote {
  return {
    purchaseNoteId: created.purchaseNoteId,
    purchaseNoteNumber: created.purchaseNoteNumber,
    purchaseNoteStatus: created.purchaseNoteStatus ?? 'CONFIRMADO',
    pdfStatus: created.pdfStatus ?? OrderPdfStatus.PENDING,
    reviewDate: created.reviewDate ?? defaults?.reviewDate ?? null,
    reservationNumber:
      created.reservationNumber ?? defaults?.reservationNumber ?? null,
    purchaseNoteObservation:
      created.purchaseNoteObservation ??
      defaults?.purchaseNoteObservation ??
      null,
    serviceIds: created.serviceIds ?? [],
    total: created.total ?? null
  };
}

/**
 * Pinta createdPurchaseNotes en filas de Alimentos y bebidas.
 * Misma coincidencia de serviceId que applyCreatedOrdersToServices.
 */
export function applyCreatedPurchaseNotesToServices(
  services: any[] | null | undefined,
  createdPurchaseNotes: CreatedPurchaseNote[] | null | undefined,
  idKey: string = 'id',
  defaults?: ApplyCreatedPurchaseNotesDefaults | null
): any[] {
  if (
    !Array.isArray(services) ||
    !services.length ||
    !createdPurchaseNotes?.length
  ) {
    return services ?? [];
  }

  for (const created of createdPurchaseNotes) {
    const serviceIds = (created.serviceIds ?? [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean);
    if (!serviceIds.length) {
      continue;
    }

    const snapshot = buildPurchaseNoteSnapshot(created, defaults);

    for (const row of services) {
      const matched = serviceIds.some((id) =>
        sameServiceId(row, id, idKey)
      );
      if (!matched) {
        continue;
      }

      row.purchaseNoteId = snapshot.purchaseNoteId;
      row.purchaseNote = {
        ...(row.purchaseNote ?? {}),
        ...snapshot
      };
      row.checked = false;
      row.selected = false;
    }
  }

  return services;
}

/** Une getPurchaseNotesByIds al modelo local de Alimentos (por purchaseNoteId / serviceIds). */
export function applyPurchaseNotesByIdsToServices(
  services: any[] | null | undefined,
  notes: ServicePurchaseNote[] | null | undefined,
  idKey: string = 'id'
): any[] {
  if (!Array.isArray(services) || !services.length || !notes?.length) {
    return services ?? [];
  }

  const byId = new Map<string, ServicePurchaseNote>();
  for (const note of notes) {
    const noteId = String(note?.purchaseNoteId ?? '').trim();
    if (noteId) {
      byId.set(noteId, note);
    }
  }

  for (const row of services) {
    const scalarId = String(row?.purchaseNoteId ?? '').trim();
    let note = scalarId ? byId.get(scalarId) : undefined;

    if (!note) {
      note = notes.find((n) =>
        (n.serviceIds ?? []).some((id) =>
          sameServiceId(row, String(id), idKey)
        )
      );
    }

    if (!note) {
      continue;
    }

    row.purchaseNoteId = note.purchaseNoteId;
    row.purchaseNote = {
      ...(row.purchaseNote ?? {}),
      ...note
    };
  }

  return services;
}

export function collectPurchaseNoteIdsFromServices(
  services: any[] | null | undefined
): string[] {
  if (!Array.isArray(services)) {
    return [];
  }

  const ids = new Set<string>();
  for (const row of services) {
    const id = String(
      row?.purchaseNote?.purchaseNoteId ?? row?.purchaseNoteId ?? ''
    ).trim();
    if (id) {
      ids.add(id);
    }
  }
  return [...ids];
}

/** Cuenta filas locales vinculadas a una NP (por id o número). */
export function countServicesByPurchaseNote(
  services: any[] | null | undefined,
  purchaseNoteId?: string | null,
  purchaseNoteNumber?: string | number | null
): number {
  if (!Array.isArray(services) || !services.length) {
    return 0;
  }

  const noteId = String(purchaseNoteId ?? '').trim();
  const noteNumber = String(purchaseNoteNumber ?? '').trim();

  return services.filter((row) => {
    const rowId = String(
      row?.purchaseNote?.purchaseNoteId ?? row?.purchaseNoteId ?? ''
    ).trim();
    if (noteId && rowId === noteId) {
      return true;
    }
    const rowNumber = String(
      row?.purchaseNote?.purchaseNoteNumber ?? ''
    ).trim();
    return Boolean(noteNumber && rowNumber && rowNumber === noteNumber);
  }).length;
}

/** Tras deletePurchaseNote: libera filas locales por purchaseNoteId (el que ya tenías). */
export function clearPurchaseNotesFromServices(
  services: any[] | null | undefined,
  purchaseNoteId?: string | null
): void {
  if (!Array.isArray(services) || !services.length) {
    return;
  }

  const noteId = String(purchaseNoteId ?? '').trim();
  if (!noteId) {
    return;
  }

  for (const row of services) {
    const rowId = String(
      row?.purchaseNote?.purchaseNoteId ?? row?.purchaseNoteId ?? ''
    ).trim();

    if (rowId !== noteId) {
      continue;
    }

    row.purchaseNoteId = null;
    row.purchaseNote = null;
    row.checked = false;
    row.selected = false;
  }
}

export function applyDeletePurchaseNoteDialogResult(
  services: any[],
  result: {
    success?: boolean;
    purchaseNoteId?: string | null;
  } | null | undefined,
  fallbackService?: any
): void {
  if (!result?.success) {
    return;
  }

  const purchaseNoteId =
    result.purchaseNoteId ??
    fallbackService?.purchaseNote?.purchaseNoteId ??
    fallbackService?.purchaseNoteId ??
    null;

  clearPurchaseNotesFromServices(services, purchaseNoteId);
}

/** Aplica update NP sobre filas con el mismo purchaseNoteId. */
export function applyPurchaseNoteUpdateToServices(
  services: any[] | null | undefined,
  purchaseNoteId: string | null | undefined,
  update: Partial<{
    purchaseNoteId?: string | null;
    purchaseNoteNumber?: string | null;
    purchaseNoteStatus?: string | null;
    reservationStatus?: string | null;
    reviewDate?: string | null;
    reservationNumber?: string | null;
    pdfStatus?: string | null;
  }> | null | undefined,
  fallbackService?: any
): void {
  if (!update || !Array.isArray(services)) {
    return;
  }

  const noteId = String(
    purchaseNoteId ?? update.purchaseNoteId ?? ''
  ).trim();

  const targets = noteId
    ? services.filter((row) => {
        const rowId = String(
          row?.purchaseNote?.purchaseNoteId ?? row?.purchaseNoteId ?? ''
        ).trim();
        return rowId === noteId;
      })
    : [];

  const rows = targets.length
    ? targets
    : fallbackService
      ? [fallbackService]
      : [];

  for (const row of rows) {
    row.purchaseNoteId = update.purchaseNoteId ?? row.purchaseNoteId ?? noteId;
    row.purchaseNote = {
      ...(row.purchaseNote ?? {}),
      ...update
    };
  }
}
